/**
 * ★★ WK — o PERÍODO PADRÃO do dashboard, e o fuso. Puro.
 *
 * Decisão do dono (30/set/2026): a tela abre **do dia 1 do mês corrente até
 * hoje** — não mais "últimos 30 dias".
 *
 * ★★ POR QUE ISTO É UM MÓDULO, e não duas linhas na página: a armadilha é de
 * FUSO, e fuso não se testa de olho.
 *
 * ⚠ O ERRO QUE ESTE MÓDULO EXISTE PARA NÃO COMETER: derivar a data de
 * `toISOString()` — que é **UTC**. Brasília é UTC−3, então entre **21:00 e
 * 23:59 do dia 30**, o UTC já está no dia 1 do mês seguinte. A tela abriria com
 * "01/10 a 01/10" enquanto no relógio da clínica ainda é 30 de setembro, e o
 * gestor veria o mês vazio sem entender por quê. O contrário também vale: às
 * 00h30 do dia 1, o UTC ainda está no dia 30 e a tela abriria no mês ANTERIOR.
 *
 * ★ A saída não é usar `getDate()` e torcer para a máquina estar em Brasília —
 * é pedir a data **no fuso da clínica**, explicitamente, ao `Intl`. Assim o
 * resultado é o mesmo num navegador em São Paulo, num servidor em UTC e no
 * emulador rodando em qualquer lugar.
 */

export const FUSO_DA_CLINICA = 'America/Sao_Paulo';

/**
 * A data de HOJE no fuso da clínica, como `yyyy-mm-dd`.
 *
 * ★ `en-CA` é o truque: é a única locale comum cujo formato curto já é
 * `YYYY-MM-DD`, que é exatamente o que o `<input type="date">` quer. Evita
 * remontar a string a partir de `formatToParts`.
 */
export function hojeNaClinica(agora = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_DA_CLINICA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(agora);
}

/**
 * O período padrão: **dia 1 do mês corrente até hoje**, no fuso da clínica.
 *
 * ★ No dia 1 o intervalo é de UM DIA só (`01/10 a 01/10`) — e é válido: o
 * `analisar` trata `inicio === fim` como qualquer outra janela.
 */
export function periodoPadrao(agora = new Date()) {
  const hoje = hojeNaClinica(agora);
  return { de: `${hoje.slice(0, 7)}-01`, ate: hoje };
}

/**
 * O atalho "últimos 30 dias": preenche os dois campos.
 *
 * ★ Ele NÃO substitui o filtro por data — só o preenche. As duas datas seguem
 * editáveis à mão depois.
 * ⚠ A subtração é feita sobre a data JÁ no fuso da clínica (montada ao meio-dia,
 * a mesma defesa da VC): tirar 30 dias de um instante UTC poderia atravessar a
 * virada e devolver um dia a mais ou a menos.
 */
export function ultimos30Dias(agora = new Date()) {
  const hoje = hojeNaClinica(agora);
  const [a, m, d] = hoje.split('-').map(Number);
  const inicio = new Date(a, m - 1, d, 12, 0, 0, 0);
  inicio.setDate(inicio.getDate() - 30);
  const z = (n) => String(n).padStart(2, '0');
  return {
    de: `${inicio.getFullYear()}-${z(inicio.getMonth() + 1)}-${z(inicio.getDate())}`,
    ate: hoje,
  };
}

/**
 * ★★ WO — A FAIXA UTC de um dia da clínica, para consultar `criadoEm` por dia.
 *
 * ⚠⚠ ESTE É O LUGAR DO FUSO, e a revisão 3 do plano WO existe por causa dele.
 *
 * A revisão 2 propunha gravar um campo `dia` ('AAAA-MM-DD') em cada interação, para a
 * consulta comparar string com string. Esse campo viria do RELÓGIO DO CLIENTE — era
 * forjável, e contradizia a regra de ouro da peça ("só conta o que o sistema grava sozinho").
 * ★ Saiu. `criadoEm` é `serverTimestamp()` e já resolve; o que mudou é que o fuso passou a
 * morar AQUI, na consulta, e não num campo.
 *
 * ★ O deslocamento é fixo em −3h: o Brasil não tem horário de verão desde 2019. ⚠ Se um dia
 * voltar, é ESTA função que muda — e só ela.
 *
 * @param referencia  um instante qualquer dentro do dia desejado
 * @returns `{ de, ate }` — Dates em UTC cobrindo 00:00:00.000 a 23:59:59.999 em São Paulo
 */
export function faixaDoDiaNaClinica(referencia = new Date()) {
  const [ano, mes, dia] = hojeNaClinica(referencia).split('-').map(Number);
  return {
    de: new Date(Date.UTC(ano, mes - 1, dia, 3, 0, 0, 0)),
    ate: new Date(Date.UTC(ano, mes - 1, dia + 1, 2, 59, 59, 999)),
  };
}

/**
 * ★★ WO lote 2b — A FAIXA UTC de um MÊS da clínica.
 *
 * Irmã da `faixaDoDiaNaClinica`, e mora aqui pelo mesmo motivo: ★ **o fuso tem UM lugar.**
 * A grade mensal lê o mês inteiro numa consulta só, e precisa da mesma conversão.
 *
 * ⚠ NÃO reusa `limitesDoMes` de `metas/calculo.js`: aquela devolve Dates no fuso do
 * NAVEGADOR (`new Date(ano, m-1, 1)`), que é o certo para o que ela faz — recortar objetos
 * já em memória. Aqui o alvo é uma consulta ao servidor por `criadoEm`, e o que importa é o
 * instante UTC em que o dia começa **na clínica**. Misturar as duas poria o fuso em dois
 * lugares, que é exatamente o que a revisão 3 veio desfazer.
 *
 * @param mes  'AAAA-MM'
 * @returns `{ de, ate }` — do 1º às 00:00:00.000 ao último dia às 23:59:59.999, em São Paulo
 */
export function faixaDoMesNaClinica(mes) {
  const [ano, m] = String(mes ?? '').split('-').map(Number);
  if (!ano || !m || m < 1 || m > 12) throw new Error(`faixaDoMesNaClinica: mês inválido: ${mes}`);
  return {
    // ★ Dia 0 do mês SEGUINTE é o último dia deste — o truque que evita a tabela de 28/29/30/31.
    de: new Date(Date.UTC(ano, m - 1, 1, 3, 0, 0, 0)),
    ate: new Date(Date.UTC(ano, m, 1, 2, 59, 59, 999)),
  };
}

/**
 * ★★ Indicadores (01/11/2026) — A FAIXA UTC de uma sequência de dias da clínica (a semana).
 *
 * Irmã das duas acima, pelo mesmo motivo: o fuso tem UM lugar. Do primeiro dia às
 * 00:00:00.000 ao último às 23:59:59.999, em São Paulo — as mesmas bordas de
 * `faixaDoDiaNaClinica`, então cada dia da semana traz exatamente o que o mês traria.
 *
 * @param de   'AAAA-MM-DD'
 * @param ate  'AAAA-MM-DD'
 */
export function faixaEntreDiasNaClinica(de, ate) {
  const [a1, m1, d1] = String(de ?? '').split('-').map(Number);
  const [a2, m2, d2] = String(ate ?? '').split('-').map(Number);
  if (!a1 || !m1 || !d1 || !a2 || !m2 || !d2) throw new Error(`faixaEntreDiasNaClinica: dias inválidos: ${de} a ${ate}`);
  return {
    de: new Date(Date.UTC(a1, m1 - 1, d1, 3, 0, 0, 0)),
    ate: new Date(Date.UTC(a2, m2 - 1, d2 + 1, 2, 59, 59, 999)),
  };
}

/** 'AAAA-MM' de um instante, no fuso da clínica. */
export const mesNaClinica = (referencia = new Date()) => hojeNaClinica(referencia).slice(0, 7);
